import { useState, useEffect } from "react";
import {
  useAccount,
  useReadContract,
  useWriteContract,
  useWaitForTransactionReceipt,
  useBlockNumber,
} from "wagmi";
import { formatEther, parseEther } from "viem";
import { CONTRACTS, ABIS, CURRENT_RPC_POOL, ENVIRONMENT } from "../contracts/config";
import { message } from "antd";
import { useNctBalance } from "./useNctBalance";

// 质押所需的NCT代币数量 (44444 NCT)
const STAKE_AMOUNT = parseEther("44444");

// 轮询间隔配置
const POLLING_INTERVAL = 30000; // 30秒轮询一次 非关键数据
const FAST_POLLING_INTERVAL = 5000; // 5秒快速轮询 关键用户信息

// 随机选择RPC  
const getRandomRPC = () => {
  const randomIndex = Math.floor(Math.random() * CURRENT_RPC_POOL.length);
  const selectedRPC = CURRENT_RPC_POOL[randomIndex];
  return selectedRPC;
};

export function useStaking() {
  const { address, isConnected } = useAccount();
  const [isStaking, setIsStaking] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(0);

  // 使用统一的NCT余额和授权hook
  const { 
    balance: nctBalance, 
    balanceWei: nctBalanceWei, 
    isLoading: isNctBalanceLoading, 
    refetch: refetchNctBalance,
    getAllowance,
    checkAllowance: checkNctAllowance,
    approve: approveNct,
    needsApproval: needsNctApproval,
    isApproving,
    approveHash,
    isApproveConfirming,
    refetchAllowance: refetchNctAllowance,
  } = useNctBalance();

  // 获取质押合约的授权额度
  const allowance = getAllowance(CONTRACTS.STAKING || '');

  // 强制刷新数据的函数
  const forceRefresh = () => {
    setLastRefreshTime(Date.now());
  };

  // get block number
  const { data: block } = useBlockNumber({
    watch: true, // 打开 websocket 订阅新块
    chainId: ENVIRONMENT.chainId, // 根据环境使用对应的链ID
  });

  const {
    data: stakeAmountData,
    isLoading: isStakeAmountLoading,
    refetch: refetchStakeAmount,
  } = useReadContract({
    address: CONTRACTS.STAKING,
    abi: ABIS.STAKING as any,
    functionName: "stakeAmount",
    query: {
      refetchInterval: POLLING_INTERVAL,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
    },
  });

  // 读取质押状态是否启用 (stakingEnabled)
  const {
    data: stakingEnabledData,
    isLoading: isStakingEnabledLoading,
    refetch: refetchStakingEnabled,
  } = useReadContract({
    address: CONTRACTS.STAKING,
    abi: ABIS.STAKING as any,
    functionName: "stakingEnabled",
    query: {
      refetchInterval: POLLING_INTERVAL,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
    },
  });

  // 读取用户已质押金额 (stakes) - 关键数据，使用快速轮询
  const {
    data: userStakeData,
    isLoading: isUserStakeLoading,
    refetch: refetchUserStake,
  } = useReadContract({
    address: CONTRACTS.STAKING,
    abi: ABIS.STAKING as any,
    functionName: "stakes",
    args: address ? [address] : undefined,
    query: {
      enabled: !!address,
      refetchInterval: FAST_POLLING_INTERVAL, // 使用快速轮询
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
      staleTime: 0, // 数据立即过期，强制刷新
    },
  });

  // 读取用户解锁状态 (unlocked) - 这是全局解锁状态，不是用户特定的
  const {
    data: userUnlockedData,
    isLoading: isUserUnlockedLoading,
    refetch: refetchUserUnlocked,
  } = useReadContract({
    address: CONTRACTS.STAKING,
    abi: ABIS.STAKING as any,
    functionName: "unlocked",
    args: [], // 移除用户地址参数，因为这是全局状态
    query: {
      refetchInterval: FAST_POLLING_INTERVAL, // 使用快速轮询
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
      staleTime: 0, // 数据立即过期，强制刷新
    },
  });

  // 读取代币合约地址 (token)
  const {
    data: tokenAddressData,
    isLoading: isTokenAddressLoading,
    refetch: refetchTokenAddress,
  } = useReadContract({
    address: CONTRACTS.STAKING,
    abi: ABIS.STAKING as any,
    functionName: "token",
    query: {
      refetchInterval: POLLING_INTERVAL * 3, // 代币地址变化较少，降低轮询频率
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  });

  // 写入合约 hooks
  const { writeContract: writeStake, data: stakeHash } = useWriteContract();
  const { writeContract: writeWithdraw, data: withdrawHash } =
    useWriteContract();

  // 等待交易确认 - 添加轮询
  const { isLoading: isStakeConfirming } = useWaitForTransactionReceipt({
    hash: stakeHash,
    query: {
      enabled: !!stakeHash,
      refetchInterval: 500, // 0.5秒轮询交易状态
    },
  });

  const { isLoading: isWithdrawConfirming } = useWaitForTransactionReceipt({
    hash: withdrawHash,
    query: {
      enabled: !!withdrawHash,
      refetchInterval: 500, // 0.5秒轮询交易状态
    },
  });

  // 检查是否需要授权
  const needsApproval = () => {
    if (!CONTRACTS.STAKING) return true;
    return needsNctApproval(CONTRACTS.STAKING, formatEther(STAKE_AMOUNT));
  };

  // 检查用户是否有足够的NCT代币
  const hasEnoughBalance = () => {
    if (!nctBalanceWei || !STAKE_AMOUNT) return false;
    return BigInt(nctBalanceWei.toString()) >= STAKE_AMOUNT;
  };

  // 检查是否可以质押
  const checkAllowance = (amount: string): boolean => {
    if (!CONTRACTS.STAKING) return false;
    return checkNctAllowance(CONTRACTS.STAKING, amount);
  };

  // 授权NCT代币给质押合约
  const approve = async () => {
    if (!isConnected || !address) {
      message.error("请先连接钱包");
      return;
    }

    if (!hasEnoughBalance()) {
      message.error("NCT代币余额不足，需要至少44444 NCT");
      return;
    }

    if (!CONTRACTS.STAKING) {
      message.error("质押合约地址未配置");
      return;
    }

    try {
      const selectedRPC = getRandomRPC();
      message.info(
        `正在授权... `
      );

      const success = await approveNct(CONTRACTS.STAKING, formatEther(STAKE_AMOUNT));
      return success;
    } catch (error: any) {
      console.error("授权失败:", error);
      message.error(`授权失败: ${error.message || "未知错误"}`);
      return false;
    }
  };

  // 质押函数
  const stake = async () => {
    if (!isConnected || !address) {
      message.error("请先连接钱包");
      return;
    }

    if (!hasEnoughBalance()) {
      message.error("NCT代币余额不足，需要至少44444 NCT");
      return;
    }

    if (needsApproval()) {
      message.error("请先授权NCT代币");
      return;
    }

    try {
      setIsStaking(true);
      const selectedRPC = getRandomRPC();
      message.info(`正在执行质押... (使用RPC: ${selectedRPC.split("/")[2]})`);

      writeStake({
        address: CONTRACTS.STAKING,
        abi: ABIS.STAKING as any,
        functionName: "stake",
        args: [],
      });
    } catch (error: any) {
      console.error("质押失败:", error);
      message.error(`质押失败: ${error.message || "未知错误"}`);
      setIsStaking(false);
    }
  };

  // 完整的质押流程（授权 + 质押）
  const stakeWithApproval = async () => {
    if (!isConnected || !address) {
      message.error("请先连接钱包");
      return;
    }

    if (!hasEnoughBalance()) {
      message.error("NCT代币余额不足，需要至少44444 NCT");
      return;
    }

    try {
      // 第一步：检查是否需要授权
      if (needsApproval()) {
        message.info("第1步：授权NCT代币给质押合约...");
        await approve();
        return; // 等待授权完成后用户再次点击
      }

      // 第二步：执行质押
      message.info("第2步：执行质押操作...");
      await stake();
    } catch (error: any) {
      console.error("质押流程失败:", error);
      message.error(`质押流程失败: ${error.message || "未知错误"}`);
    }
  };

  // 提取函数
  const withdraw = async () => {
    if (!isConnected || !address) {
      message.error("请先连接钱包");
      return;
    }

    // 检查解锁状态
    if (!userUnlockedData) {
      message.error("质押尚未解锁，无法提取");
      return;
    }

    try {
      const selectedRPC = getRandomRPC();
      message.info(`正在提取质押... (使用RPC: ${selectedRPC.split("/")[2]})`);

      writeWithdraw({
        address: CONTRACTS.STAKING,
        abi: ABIS.STAKING as any,
        functionName: "withdraw",
        args: [],
      });
    } catch (error: any) {
      console.error("提取失败:", error);
      message.error(`提取失败: ${error.message || "未知错误"}`);
    }
  };

  // 刷新授权额度
  const refetchAllowance = () => {
    if (CONTRACTS.STAKING) {
      refetchNctAllowance(CONTRACTS.STAKING);
    }
  };

  // 监听交易状态变化
  useEffect(() => {
    if (approveHash && !isApproveConfirming) {
      // 立即强制刷新相关数据
      setTimeout(() => {
        refetchAllowance();
        refetchNctBalance();
        forceRefresh();
      }, 500);

      // 触发自定义事件，通知页面授权完成
      window.dispatchEvent(new CustomEvent("approvalCompleted"));
    }
  }, [approveHash, isApproveConfirming, refetchNctBalance]);

  useEffect(() => {
    if (stakeHash && !isStakeConfirming) {
      setIsStaking(false);
      message.success("质押成功！");

      // 立即强制刷新相关数据
      setTimeout(() => {
        refetchUserStake();
        refetchNctBalance();
        refetchAllowance();
        refetchUserUnlocked();
        forceRefresh();
      }, 500);

      // 触发自定义事件，通知页面质押真正成功
      window.dispatchEvent(new CustomEvent("stakeCompleted", {
        detail: { stakeHash }
      }));
    }
  }, [
    stakeHash,
    isStakeConfirming,
    refetchUserStake,
    refetchNctBalance,
    refetchUserUnlocked,
  ]);

  useEffect(() => {
    if (withdrawHash && !isWithdrawConfirming) {
      message.success("提取成功！");

      // 立即强制刷新相关数据
      setTimeout(() => {
        refetchUserStake();
        refetchNctBalance();
        refetchUserUnlocked();
        forceRefresh();
      }, 500);
    }
  }, [
    withdrawHash,
    isWithdrawConfirming,
    refetchUserStake,
    refetchNctBalance,
    refetchUserUnlocked,
  ]);

  // 刷新所有数据
  const refetchAll = () => {
    refetchStakeAmount();
    refetchStakingEnabled();
    refetchUserStake();
    refetchUserUnlocked();
    refetchTokenAddress();
    refetchNctBalance();
    refetchAllowance();
    forceRefresh();
  };

  // 添加定期强制刷新机制
  useEffect(() => {
    if (!isConnected || !address) return;

    const interval = setInterval(() => {
      refetchNctBalance();
      refetchAllowance();
      refetchUserUnlocked();
    }, 10000);

    return () => clearInterval(interval);
  }, [
    isConnected,
    address,
    refetchUserStake,
    refetchNctBalance,
    refetchUserUnlocked,
  ]);

  // 监听地址变化，立即刷新数据
  useEffect(() => {
    if (address) {
      setTimeout(() => {
        refetchAll();
      }, 100);
    }
  }, [address]);


  return {
    // 合约数据
    stakeAmount: stakeAmountData ? formatEther(stakeAmountData as bigint) : "0",
    stakingEnabled: stakingEnabledData as boolean,
    userStake: userStakeData ? formatEther(userStakeData as bigint) : "0",
    userUnlocked: userUnlockedData as boolean,
    tokenAddress: tokenAddressData as string,
    nctBalance: nctBalance,
    allowance: allowance,

    // 加载状态
    isLoading:
      isStakeAmountLoading ||
      isStakingEnabledLoading ||
      isUserStakeLoading ||
      isUserUnlockedLoading ||
      isTokenAddressLoading ||
      isNctBalanceLoading,
    isApproving: isApproving,
    isStaking: isStaking || isStakeConfirming,
    isWithdrawing: isWithdrawConfirming,

    // 状态检查
    needsApproval: needsApproval(),
    hasEnoughBalance: hasEnoughBalance(),
    canStake: !needsApproval() && hasEnoughBalance(),

    // 操作函数
    approve,
    stake,
    stakeWithApproval,
    withdraw,
    refetchAll,

    // 交易哈希
    approveHash,
    stakeHash,
    withdrawHash,

    // 常量
    requiredAmount: "44444",

    // 调试信息
    lastRefreshTime,
  };
}
